import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { RefreshControl } from 'react-native';
import { useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

import { useUserContext } from '../../../common/utils/context';
import { isDocketHandledForMyDay } from '../../../common/utils/myDay/driverDaySteps';
import { getLocalTime } from '../../../common/utils/time/time';
import { EmptyState, ListRow, Screen, SecondaryButton, SectionTitle } from '../../design/components';
import useTodayDateKey from '../../hooks/useTodayDateKey';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { DRIVER_ROUTE } from './driverRoutes';
import { GET_MY_RUNS } from './myRunsQueries';

const PER_PAGE = 10;

const MyRunsView = () => {
	const { user, tenant } = useUserContext();
	const navigation = useNavigation();
	const todayDateKey = useTodayDateKey(tenant?.timezone);
	const [page, setPage] = useState(0);
	const { pastFilter, dayFilter } = useMemo(() => {
		const today = moment.tz(todayDateKey, tenant?.timezone || 'Australia/Melbourne').startOf('day');
		return {
			pastFilter: { driver: user?.id, plannedToDate: today.clone().subtract(1, 'day').unix() },
			dayFilter: { driver: user?.id, plannedFromDate: today.unix(), plannedToDate: today.clone().add(1, 'day').unix() },
		};
	}, [tenant?.timezone, todayDateKey, user?.id]);
	const history = useQuery(GET_MY_RUNS, {
		variables: { page, perPage: PER_PAGE, filter: pastFilter },
		skip: !user?.id,
		fetchPolicy: 'cache-and-network',
	});
	const days = useQuery(GET_MY_RUNS, {
		variables: { filter: dayFilter },
		skip: !user?.id,
		fetchPolicy: 'cache-and-network',
	});
	const { refetch: refetchHistory } = history;
	const { refetch: refetchDays } = days;
	const refetch = useCallback(() => Promise.all([refetchHistory(), refetchDays()]), [refetchHistory, refetchDays]);
	useEffect(() => setPage(0), [todayDateKey, user?.id]);
	useFocusEffect(useCallback(() => { refetch().catch(() => {}); }, [refetch]));
	const [refresh, isRefreshing] = useRefreshPage(refetch);
	const dayRuns = days.data?.getServiceRuns?.items || [];
	const todaysRuns = dayRuns.filter(run => getLocalTime(tenant?.timezone, run.planned).format('YYYY-MM-DD') === todayDateKey);
	// Match My day's completion rule, including stops marked Couldn't complete.
	// A failed/stale day refresh must never unlock tomorrow's preview.
	const canPreviewTomorrow = Boolean(days.data) && !days.loading && !days.error && todaysRuns.every(run => (
		run.serviceDockets?.length > 0 && run.serviceDockets.every(isDocketHandledForMyDay)
	));
	const tomorrowDateKey = moment.tz(todayDateKey, tenant?.timezone || 'Australia/Melbourne').add(1, 'day').format('YYYY-MM-DD');
	const tomorrowRuns = canPreviewTomorrow ? dayRuns.filter(run => (
		getLocalTime(tenant?.timezone, run.planned).format('YYYY-MM-DD') === tomorrowDateKey
	)) : [];
	const pastRuns = history.data?.getServiceRuns?.items || [];
	const renderRun = run => (
		<ListRow
			key={run.id}
			title={run.route?.name || 'Service run'}
			overline={getLocalTime(tenant?.timezone, run.planned).format('dddd D MMMM YYYY')}
			subtitle={[run.vehicle?.name, `${run.serviceDockets?.length || 0} ${run.serviceDockets?.length === 1 ? 'stop' : 'stops'}`].filter(Boolean).join(' · ')}
			onPress={() => navigation.push(DRIVER_ROUTE.MY_RUN_OVERVIEW, { id: run.id })}
		/>
	);

	return (
		<Screen
			backLabel="Home"
			heading="My runs"
			subheading="Past runs are view-only."
			isLoading={history.loading && !history.data}
			error={history.error ? 'Your past runs could not be loaded.' : undefined}
			onRetry={refetch}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refresh} />}
		>
			{tomorrowRuns.length > 0 && <SectionTitle title="Tomorrow" />}
			{tomorrowRuns.map(renderRun)}
			<SectionTitle title="Past runs" />
			{pastRuns.map(renderRun)}
			{!pastRuns.length && <EmptyState title="No past runs" message="Your past runs will appear here." icon="truck" />}
			{page > 0 && <SecondaryButton label="Newer runs" onPress={() => setPage(value => value - 1)} />}
			{(page + 1) * PER_PAGE < (history.data?.getServiceRuns?.count || 0) && (
				<SecondaryButton label="Older runs" onPress={() => setPage(value => value + 1)} />
			)}
		</Screen>
	);
};

export default MyRunsView;
